import { FC, memo, useRef } from 'react';
import { Box, Button, SxStyleProp, Text } from 'theme-ui';
import { SceneTransition } from 'types/Scene';
import Icon, { IconTypes } from 'shared/Icon';
import { TransitionType } from 'types/Transitions';
import { useClickAway, useToggle } from 'react-use';

import Tooltip, { placementsList } from '@picsart/rc/old/Tooltip';

import { useTranslator } from '@picsart/rc/services/localization';

export type TransitionCardProps = Pick<SceneTransition, 'id' | 'is_paid' | 'thumbnail_url'> & {
  name: string;
  selected?: boolean;
  selectHandler: (id: SceneTransition['id']) => void;
  applyToAllSceneHandler: () => void;
  type: TransitionType;
};

const WIDTH = 154;
const HEIGHT = 154;
const BORDER_WIDTH = 2;
const INNER_BORDER_WIDTH = 4;

const cardStyles: SxStyleProp = {
  all: 'unset',
  display: 'block',
  position: 'relative',
  width: WIDTH,
  height: HEIGHT,
  cursor: 'pointer',
  boxSizing: 'border-box',
  borderRadius: 'default',
  borderStyle: 'solid',
  borderWidth: BORDER_WIDTH,
  borderColor: 'transparent',
  '&:hover': {
    borderColor: 'grey3'
  },
  '.transition-card-tooltip': {
    i: {
      width: '19px',
      height: '19px'
    },
    position: 'absolute',
    bottom: '10px',
    right: '10px'
  }
};

const selectedCardStyles = {
  ...cardStyles,
  cursor: 'default',
  borderColor: 'primary !important'
};

const previewContainerStyle: SxStyleProp = {
  overflow: 'hidden',
  borderRadius: 'default',
  width: WIDTH,
  height: HEIGHT,
  m: `${-BORDER_WIDTH}px`
};

const selectedPreviewContainerStyle: SxStyleProp = {
  ...previewContainerStyle,
  width: WIDTH - 2 * (BORDER_WIDTH + INNER_BORDER_WIDTH),
  height: HEIGHT - 2 * (BORDER_WIDTH + INNER_BORDER_WIDTH),
  borderRadius: 2,
  m: `${INNER_BORDER_WIDTH}px`
};

const previewStyles: SxStyleProp = { display: 'block', width: WIDTH, height: HEIGHT };

const nameStyles: SxStyleProp = {
  position: 'absolute',
  bottom: '10px',
  left: '10px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 50,
  maxWidth: 100,
  py: 1,
  px: 2,
  fontSize: '9px',
  fontWeight: 'button',
  lineHeight: 1.2,
  bg: 'grey6',
  color: 'background',
  borderRadius: 'alert',
  userSelect: 'none'
};

const buttonStyle: SxStyleProp = {
  position: 'absolute',
  top: `${10 - (BORDER_WIDTH + INNER_BORDER_WIDTH)}px`,
  right: `${10 - (BORDER_WIDTH + INNER_BORDER_WIDTH)}px`,
  padding: 0,
  borderRadius: 'xs',
  bg: 'black',
  zIndex: 100
};

const selectedButtonStyle: SxStyleProp = {
  ...buttonStyle,
  top: '10px',
  right: '10px'
};

export const TransitionCard: FC<TransitionCardProps> = memo(
  ({ name, selected, selectHandler, id, thumbnail_url, is_paid, type, applyToAllSceneHandler }) => {
    const previewRef = useRef(null);
    const [isHover, setHover] = useToggle(false);
    const translator = useTranslator();

    const handlePointerEnter = () => {
      previewRef.current.play();
      setHover(true);
    };
    const handlePointerLeave = () => {
      previewRef.current.pause();
      previewRef.current.currentTime = 0;
      setHover(false);
    };

    return (
      <div
        onPointerEnter={handlePointerEnter}
        onPointerLeave={handlePointerLeave}
        sx={selected ? selectedCardStyles : cardStyles}
        onClick={() => selected || selectHandler(id)}
        data-test={`transition-tool_select-${type}`}
      >
        <div sx={isHover || selected ? selectedPreviewContainerStyle : previewContainerStyle}>
          <video loop sx={previewStyles} ref={previewRef} src={thumbnail_url} />
          {isHover && (
            <TransitionCardMenu applyToAll={applyToAllSceneHandler} isSelected={selected} />
          )}
        </div>
        <Text sx={nameStyles} className="safari-fix-text-white">
          {name}
        </Text>
        {is_paid && (
          <Tooltip
            placement={placementsList.right}
            content={translator('This is a premium transition')}
          >
            <span className="transition-card-tooltip">
              <Icon name="gold-circle-gen" />
            </span>
          </Tooltip>
        )}
      </div>
    );
  }
);

const TransitionCardMenu: FC<{ applyToAll: () => void; isSelected: boolean }> = ({
  applyToAll
}) => {
  const [isOpen, setOpen] = useToggle(false);
  const ref = useRef();
  const translator = useTranslator();
  useClickAway(ref, () => {
    setOpen(false);
  });
  return (
    <div ref={ref}>
      <Button
        onClick={e => {
          e.stopPropagation();
          setOpen(!isOpen);
        }}
        variant="inverted"
        sx={selectedButtonStyle}
      >
        <Icon
          name="dots-editor"
          type={IconTypes.White}
          sx={{
            width: '16px',
            height: '12px'
          }}
        />
      </Button>
      {isOpen && (
        <Box
          sx={{
            position: 'absolute',
            bg: 'white',
            zIndex: 'toolPanel',
            width: '128px',
            border: '1px solid ',
            borderColor: 'grey3',
            p: 3,
            borderRadius: 'button',
            top: '50%',
            transform: 'translate(-50%,-50%)',
            left: '50%'
          }}
        >
          <Button
            variant="secondary"
            onClick={e => {
              e.stopPropagation();
              applyToAll();
              setOpen(false);
            }}
            sx={{
              width: '100%',
              border: 'none',
              fontSize: 2,
              justifyContent: 'center',
              zIndex: 'toolPanel',
              ':hover': {
                bg: 'grey2'
              }
            }}
          >
            {translator('Apply to all')}
          </Button>
        </Box>
      )}
    </div>
  );
};
